import { useSnackbar } from "notistack";
import { setBlogItems } from "src/slices/blog";

const { server } = require("src/coreConfig");
const { default: useAsyncRequest } = require("./useAsyncRequest");
const { useSelector, useDispatch } = require("src/store");

const blogUrls = {
  post: {
    home: `${server}/api/v2/blog/post/home/`,
    list: `${server}/api/v2/blog/post/`,
    detail: `${server}/api/v2/blog/post/detail/`,
    update: `${server}/api/v2/blog/post/update/`,
    create: `${server}/api/v2/blog/post/create/`,
    delete: `${server}/api/v2/blog/post/delete/`,
    addImage: `${server}/api/v2/blog/post/uploadImage/`,
    updateImage: `${server}/api/v2/blog/post/updateImage/`,
    deleteImage: `${server}/api/v2/blog/post/deleteImage/`,
  },
  page: {
    list: `${server}/api/v2/blog/page/`,
    detail: `${server}/api/v2/blog/page/detail/`,
    getPublicFile: `${server}/api/v2/blog/page/getPublicFile/`,
  },
  category: {
    list: `${server}/api/v2/blog/category/`,
    detail: `${server}/api/v2/blog/category/detail/`,
    update: `${server}/api/v2/blog/category/update/`,
    create: `${server}/api/v2/blog/category/create/`,
    delete: `${server}/api/v2/blog/category/delete/`,
  },
  video: {
    detail: `${server}/api/v2/blog/video/detail/`,
    add: `${server}/api/v2/blog/video/add/`,
    update: `${server}/api/v2/blog/video/update/`,
    delete: `${server}/api/v2/blog/video/delete/`,
    list: `${server}/api/v2/blog/video/`,
  },
  banner: {
    add: `${server}/api/v2/blog/banner/add/`,
    update: `${server}/api/v2/blog/banner/update/`,
    delete: `${server}/api/v2/blog/banner/delete/`,
    list: `${server}/api/v2/blog/banner/`,
  },
  announcement: {
    current: `${server}/api/v2/blog/announcement/current/`,
    add: `${server}/api/v2/blog/announcement/add/`,
    update: `${server}/api/v2/blog/announcement/update/`,
    delete: `${server}/api/v2/blog/announcement/delete/`,
    list: `${server}/api/v2/blog/announcement/`,
  },
};

const useBlog = () => {
  const { api } = useAsyncRequest();
  const { currentCustomer } = useSelector((state) => state.customers);
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const getMainBlog = async () => {
    try {
      const response = await api(blogUrls.page.list, {
        customer_id: currentCustomer.id,
      });
      if (response.data.res === 1) {
        dispatch(
          setBlogItems({
            ...response.data.data,
            loaded: true,
            loading: false,
          }),
        );
        return response.data.data;
      } else {
        enqueueSnackbar(
          response.data?.message || "Error cargando las noticias",
          { variant: "error" },
        );
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getPosts = async (pageOrOptions) => {
    try {
      const options =
        typeof pageOrOptions === "object" && pageOrOptions !== null
          ? pageOrOptions
          : { page: pageOrOptions };

      const payload = {
        customer_id: currentCustomer.id,
        page: options.page || 1,
      };

      if (options.pageSize) payload.page_size = options.pageSize;
      if (options.categoryId) payload.category_id = options.categoryId;
      if (options.search && String(options.search).trim()) {
        payload.search = String(options.search).trim();
      }

      const response = await api(blogUrls.post.list, {
        ...payload,
      });
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(
          response.data?.message || "Error cargando las noticias",
          { variant: "error" },
        );
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getHomeFeed = async (params = {}) => {
    try {
      const response = await api(blogUrls.post.home, {
        customer_id: currentCustomer.id,
        posts_limit: params.postsLimit || 6,
        videos_limit: params.videosLimit || 3,
      });
      if (response.data.res === 1) {
        return response.data.data;
      }
      enqueueSnackbar(
        response.data?.message || "Error cargando el resumen de noticias",
        { variant: "error" },
      );
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getPost = async (id) => {
    try {
      const response = await api(blogUrls.post.detail, {
        customer_id: currentCustomer.id,
        id,
      });
      if (response.data.res === 1) {
        dispatch(setBlogItems({ currentPost: response.data.data }));
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error cargando la noticia", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const pageDetail = async (slug) => {
    try {
      const response = await api(blogUrls.page.detail, {
        customer_id: currentCustomer.id,
        slug,
      });
      if (response.data.res === 1) {
        dispatch(setBlogItems({ currentPage: response.data.data }));
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error cargando la página", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getPagePublicFile = async (data) => {
    try {
      const response = await api(blogUrls.page.getPublicFile, {
        customer_id: currentCustomer.id,
        ...data,
      });
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(
          response.data?.message || "Error cargando los archivos",
          { variant: "error" },
        );
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const updatePost = async (postData) => {
    try {
      const response = await api(
        blogUrls.post.update,
        {
          customer_id: currentCustomer.id,
          ...postData,
        },
        {},
        "updating post",
      );
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error updating the post", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const createPost = async (postData) => {
    try {
      const response = await api(
        blogUrls.post.create,
        {
          customer_id: currentCustomer.id,
          ...postData,
        },
        {},
        "creating post",
      );
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error creating the post", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const deletePost = async (id) => {
    try {
      const response = await api(
        blogUrls.post.delete,
        {
          customer_id: currentCustomer.id,
          id,
        },
        {},
        "deleting post",
      );
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error deleting the post", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const addImage = async (data) => {
    try {
      const formData = new FormData();
      formData.append("customer_id", currentCustomer.id);
      Object.keys(data).forEach((key) => {
        if (data[key] !== undefined && data[key] !== null) {
          if (
            key === "image" &&
            Array.isArray(data[key]) &&
            data[key].length > 0
          ) {
            formData.append(key, data[key][0]);
          } else if (key !== "image") {
            formData.append(key, data[key]);
          }
        }
      });

      const response = await api(blogUrls.post.addImage, formData);
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error adding the image", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const updateImage = async (data) => {
    try {
      const formData = new FormData();
      formData.append("customer_id", currentCustomer.id);
      formData.append("id", data.id);
      Object.keys(data).forEach((key) => {
        if (data[key] !== undefined && data[key] !== null && key !== "id") {
          if (
            key === "image" &&
            Array.isArray(data[key]) &&
            data[key].length > 0
          ) {
            formData.append(key, data[key][0]);
          } else if (key !== "image") {
            formData.append(key, data[key]);
          }
        }
      });
      const response = await api(blogUrls.post.updateImage, formData);
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error updating the image", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const deleteImage = async (id) => {
    try {
      const response = await api(
        blogUrls.post.deleteImage,
        {
          customer_id: currentCustomer.id,
          id,
        },
        {},
        "deleting image",
      );
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error deleting the image", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const categoryList = async () => {
    try {
      const response = await api(blogUrls.category.list, {
        customer_id: currentCustomer.id,
      });
      if (!response?.data) {
        enqueueSnackbar("No se pudieron cargar las categorías", {
          variant: "error",
        });
        return null;
      }
      if (response.data.res === 1) {
        dispatch(setBlogItems({ categories: response.data.data }));
        return response.data.data;
      } else {
        enqueueSnackbar(
          response.data?.message || "Error cargando las categorías",
          {
            variant: "error",
          },
        );
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const createCategory = async (categoryData) => {
    try {
      const response = await api(
        blogUrls.category.create,
        {
          customer_id: currentCustomer.id,
          ...categoryData,
        },
        {},
        "creating category",
      );
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(
          response.data?.message || "Error creating the category",
          {
            variant: "error",
          },
        );
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const addVideo = async (videoData) => {
    try {
      const response = await api(
        blogUrls.video.add,
        {
          customer_id: currentCustomer.id,
          ...videoData,
        },
        {},
        "adding video",
      );
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error adding the video", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const deleteVideo = async (id) => {
    try {
      const response = await api(
        blogUrls.video.delete,
        {
          customer_id: currentCustomer.id,
          id,
        },
        {},
        "deleting video",
      );
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error deleting the video", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const updateVideo = async (videoData) => {
    try {
      const response = await api(
        blogUrls.video.update,
        {
          customer_id: currentCustomer.id,
          ...videoData,
        },
        {},
        "updating video",
      );
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error updating the video", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const listVideos = async () => {
    try {
      const response = await api(blogUrls.video.list, {
        customer_id: currentCustomer.id,
      });
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error cargando los videos", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const addBanner = async (bannerData) => {
    try {
      var formData = new FormData();
      formData.append("customer_id", currentCustomer.id);
      for (const key in bannerData) {
        formData.append(key, bannerData[key]);
      }
      const response = await api(
        blogUrls.banner.add,
        formData,
        {
          "Content-Type": "multipart/form-data",
        },
        "adding banner",
      );

      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error adding the banner", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const updateBanner = async (bannerData) => {
    try {
      var formData = new FormData();
      formData.append("customer_id", currentCustomer.id);
      for (const key in bannerData) {
        formData.append(key, bannerData[key]);
      }
      const response = await api(
        blogUrls.banner.update,
        formData,
        {
          "Content-Type": "multipart/form-data",
        },
        "updating banner",
      );

      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error updating the banner", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const deleteBanner = async (id) => {
    try {
      const response = await api(
        blogUrls.banner.delete,
        {
          customer_id: currentCustomer.id,
          id,
        },
        {},
        "deleting banner",
      );
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(response.data?.message || "Error deleting the banner", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const listBanners = async () => {
    try {
      const response = await api(blogUrls.banner.list, {
        customer_id: currentCustomer.id,
      });
      if (response.data.res === 1) {
        return response.data.data;
      } else {
        enqueueSnackbar(
          response.data?.message || "Error cargando los banners",
          {
            variant: "error",
          },
        );
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getCurrentAnnouncement = async ({
    mode = "user",
    moduleKey = "",
  } = {}) => {
    try {
      const response = await api(blogUrls.announcement.current, {
        customer_id: currentCustomer.id,
        mode,
        module_key: moduleKey,
      });
      if (response.data.res === 1) {
        return response.data.data;
      }
      enqueueSnackbar(response.data?.message || "Error cargando el anuncio", {
        variant: "error",
      });
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  return {
    getMainBlog,
    getHomeFeed,
    getPosts,
    getPost,
    updatePost,
    deletePost,
    createPost,
    addImage,
    updateImage,
    deleteImage,
    page: {
      detail: pageDetail,
      getPublicFile: getPagePublicFile,
    },
    category: {
      list: categoryList,
      create: createCategory,
    },
    video: {
      add: addVideo,
      delete: deleteVideo,
      update: updateVideo,
      list: listVideos,
    },
    banner: {
      add: addBanner,
      update: updateBanner,
      delete: deleteBanner,
      list: listBanners,
    },
    announcement: {
      current: getCurrentAnnouncement,
    },
  };
};

export default useBlog;
